import { Plus, Trash2 } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { useI18n } from '../../../i18n/useI18n';
import { ChatbotSettingsCardFooter } from './ChatbotSettingsCardFooter';
import './ChatbotCorsOriginsCard.css';

type ChatbotCorsOriginsCardProps = {
  origins: string[];
  newOrigin: string;
  canAddOrigin: boolean;
  isDirty: boolean;
  isSaving: boolean;
  discardLabel: string;
  saveLabel: string;
  onNewOriginChange: (value: string) => void;
  onAddOrigin: () => void;
  onRemoveOrigin: (origin: string) => void;
  onDiscard: () => void;
  onSave: () => void;
};

export function ChatbotCorsOriginsCard({
  origins,
  newOrigin,
  canAddOrigin,
  isDirty,
  isSaving,
  discardLabel,
  saveLabel,
  onNewOriginChange,
  onAddOrigin,
  onRemoveOrigin,
  onDiscard,
  onSave,
}: ChatbotCorsOriginsCardProps) {
  const { translate } = useI18n();

  return (
    <div className="chatbot-settings__card">
      <div className="chatbot-settings__body">
        <h2 className="chatbot-settings__title">
          {translate('chatbots.settings.cors.title')}
        </h2>

        <p className="chatbot-settings__cors-hint">
          {translate('chatbots.settings.cors.testingHint', {
            origin: window.location.origin,
          })}
        </p>

        <div className="chatbot-settings__origins">
          <p className="chatbot-settings__label">
            {translate('chatbots.settings.cors.originsLabel')}
          </p>
          {origins.length === 0 ? (
            <p className="chatbot-settings__empty">
              {translate('chatbots.settings.cors.empty')}
            </p>
          ) : (
            <ul className="chatbot-settings__origin-list">
              {origins.map((origin) => (
                <li key={origin} className="chatbot-settings__origin-item">
                  <span className="chatbot-settings__origin-value">
                    {origin}
                  </span>
                  <button
                    type="button"
                    className="chatbot-settings__origin-remove"
                    aria-label={translate(
                      'chatbots.settings.cors.removeOrigin',
                      { origin },
                    )}
                    onClick={() => onRemoveOrigin(origin)}
                    disabled={isSaving}
                  >
                    <Trash2 strokeWidth={2} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="chatbot-settings__add-origin">
          <p className="chatbot-settings__label">
            {translate('chatbots.settings.cors.addOriginsLabel')}
          </p>
          <div className="chatbot-settings__add-origin-row">
            <input
              className="chatbot-settings__origin-input"
              value={newOrigin}
              onChange={(event) => onNewOriginChange(event.target.value)}
              placeholder={translate(
                'chatbots.settings.cors.originPlaceholder',
              )}
              aria-label={translate('chatbots.settings.cors.originInputLabel')}
              disabled={isSaving}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onAddOrigin();
                }
              }}
            />
            <LiksButton
              type="button"
              onClick={onAddOrigin}
              disabled={!canAddOrigin || isSaving}
            >
              <Plus strokeWidth={2} aria-hidden />
              {translate('chatbots.settings.cors.addOrigin')}
            </LiksButton>
          </div>
        </div>
      </div>

      <ChatbotSettingsCardFooter
        isDirty={isDirty}
        isSaving={isSaving}
        isSaveDisabled={!isDirty || isSaving}
        discardLabel={discardLabel}
        saveLabel={saveLabel}
        onDiscard={onDiscard}
        onSave={onSave}
      />
    </div>
  );
}
